import { api, apiJson } from '@/api/client'
import { clearTokens, getTokens, setTokens } from '@/lib/auth-tokens'
// Importing the leaf module directly (not '@/features/platform' or its
// index.ts barrel) avoids pulling in PlatformTenantProvider/context.tsx —
// tenant-view-marker.ts has zero imports of its own, so this edge cannot
// participate in an auth<->platform import cycle.
import { clearTenantViewMarker } from '@/features/platform/tenant-view-marker'

/**
 * AF-049 / cross-stack drift fix — auth transport.
 *
 * Go's JWT middleware (`internal/auth/jwt_middleware.go::bearerToken`)
 * reads the access token only from the Authorization header — never
 * from the `antifraud_access_token` cookie that the auth handler also
 * sets. The React side keeps the tokens client-side (see
 * `lib/auth-tokens.ts`) and lets `api/client.ts` attach the bearer
 * header on every request.
 *
 * Login response shape matches Go's `LoginResponse` (see
 * `internal/api/auth_handler.go`).
 */

export interface LoginPayload {
  email: string
  password: string
}

export interface AuthUser {
  id: string
  email: string
  first_name?: string
  last_name?: string
  display_name?: string
  role?: string
  status: string
}

export interface LoginResponse {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
  user: AuthUser
}

/**
 * AF-401 — when BVN step-up is required, /auth/login returns this
 * shape instead of the full token pair. The client renders the OTP
 * step and on success calls /auth/bvn-verify which returns the
 * normal LoginResponse.
 */
export interface BVNChallengeResponse {
  requires_bvn_step_up: true
  challenge_id: string
  phone_masked: string
  expires_at: string
}

export type LoginResult = LoginResponse | BVNChallengeResponse

export function isBVNChallenge(r: LoginResult): r is BVNChallengeResponse {
  return (r as BVNChallengeResponse).requires_bvn_step_up === true
}

export async function login(payload: LoginPayload): Promise<LoginResult> {
  // Login takes credentials in the body — do NOT attach a stale
  // bearer header. `skipBearer` opts out of the interceptor.
  const res = await apiJson<LoginResult>('/auth/login', {
    method: 'POST',
    body: payload,
    skipBearer: true,
    skipAuthRefresh: true,
  })
  // Only seal tokens on the no-step-up path. Step-up responses have
  // no access_token; the OTP verify call seals tokens instead.
  if (!isBVNChallenge(res)) {
    setTokens({
      access_token: res.access_token,
      refresh_token: res.refresh_token,
      expires_in: res.expires_in,
    })
  }
  return res
}

// AF-401 — BVN step-up endpoints. Both run with skipBearer because
// the user isn't authenticated yet.

export interface BVNVerifyPayload {
  challenge_id: string
  otp: string
}

export async function bvnVerify(payload: BVNVerifyPayload): Promise<LoginResponse> {
  const res = await apiJson<LoginResponse>('/auth/bvn-verify', {
    method: 'POST',
    body: payload,
    skipBearer: true,
    skipAuthRefresh: true,
  })
  setTokens({
    access_token: res.access_token,
    refresh_token: res.refresh_token,
    expires_in: res.expires_in,
  })
  return res
}

export async function bvnResend(challengeID: string): Promise<void> {
  await apiJson<void>('/auth/bvn-resend', {
    method: 'POST',
    body: { challenge_id: challengeID },
    skipBearer: true,
    skipAuthRefresh: true,
  })
}

/**
 * AF-407 — admin-driven password reset request. The user enters
 * their email and the backend dispatches a reset link if the
 * account exists. Response is intentionally generic so an attacker
 * cannot enumerate emails. Runs with skipBearer because the user
 * is unauthenticated.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  await apiJson<void>('/auth/forgot-password', {
    method: 'POST',
    body: { email },
    skipBearer: true,
    skipAuthRefresh: true,
  })
}

/**
 * Best-effort logout. Calls Go's `/auth/logout` with the access token
 * in the Authorization header (so the server can revoke it) and the
 * refresh token in the body (so the server can revoke that too).
 *
 * Always clears the local store, even if the server call fails — a
 * locally-logged-out client is the only behaviour that matters for
 * UX. Server-side cleanup retries via the next session anyway.
 *
 * Also clears the platform tenant-view breadcrumb (sessionStorage,
 * see features/platform/tenant-view-marker.ts) here, symmetric with
 * clearTokens() above. useLogout's mutationFn calls this function
 * before EITHER of its two post-logout navigations: the SPA
 * `navigate('/login')` case (where PlatformTenantProvider's unmount
 * effect would also clear the marker) AND the OIDC
 * `window.location.href = '/api/v1/auth/oidc/end-session'` hard
 * navigation, where the JS runtime tears down before React ever gets
 * to run that unmount effect. Clearing here — independent of
 * component lifecycle — is what makes the marker cleanup unconditional
 * on logout regardless of which branch runs afterward. Without it, a
 * stale marker from admin A's tenant-view session would survive the
 * OIDC round-trip in sessionStorage and be misattributed to admin B
 * if they sign in on the same tab/workstation afterward.
 */
export async function logout(): Promise<void> {
  const tokens = getTokens()
  if (tokens) {
    try {
      await api('/auth/logout', {
        method: 'POST',
        body: { refresh_token: tokens.refresh_token },
        skipAuthRefresh: true,
      })
    } catch {
      // Swallow — we still want to clear the local store.
    }
  }
  clearTokens()
  clearTenantViewMarker()
}
